import { FC, useMemo } from 'react';
import { alpha, Box, type Theme, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Markdown } from '@components/Markdown';
import { RemediationDecision } from '@libs/constants';
import { type SingularKeys, useTrans } from '@libs/i18n';
import { MAX_LIST_LEVEL, type ProseChange, type ProseDocument } from '@libs/types';
import { SparkleIcon } from '@phosphor-icons/react';

import { computeListOrdinals } from '../utils/list-ordinals';
import { buildOriginalSegments } from '../utils/original-highlights';
import { planDeletions } from '../utils/proposed-deletions';
import { useScrollToSelected } from '../utils/use-scroll-to-selected';

import { RemediationFigure } from './RemediationFigure';
import { RemediationTable } from './RemediationTable';

/** Serif body type — the design renders both panes as a document, not as UI. */
const DOC_FONT = 'Georgia, "Times New Roman", serif';

/**
 * Indent, in MUI spacing units, for a list item at depth 0/1/2. `pl` maps to
 * `padding-inline-start`, which is what keeps a nested item indented from its
 * own edge in both LTR and RTL rather than always from the physical left.
 */
const LIST_INDENT = [2, 4, 6] as const;

/**
 * Fill for an original-pane mark: red for a removal, orange for a rewording.
 *
 * The two hues are neighbours, so the removal is tinted lighter than the
 * rewording — at one shared alpha they read as one warm smudge. Shared with the
 * legend so the swatch cannot drift from the thing it explains.
 */
function markTint(theme: Theme, removed: boolean): string {
  return removed ? alpha(theme.palette.red[500], 0.15) : alpha(theme.palette.orange[500], 0.24);
}

/**
 * Footnote-style numbered marker. Clicking it focuses the change in the rail;
 * its ring colour encodes the decision so the document itself shows review
 * progress without needing the rail.
 */
export const Pin: FC<{
  n: number;
  decision: RemediationDecision;
  selected: boolean;
  onClick: () => void;
}> = observer(({ n, decision, selected, onClick }) => {
  const theme = useTheme();
  const accepted = decision === RemediationDecision.ACCEPTED;
  const rejected = decision === RemediationDecision.REJECTED;

  const ring = accepted
    ? theme.palette.green[700]
    : rejected
      ? theme.palette.text.tertiary
      : theme.palette.ocean[700];

  return (
    <Box
      component="button"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'super',
        width: 18,
        height: 18,
        minWidth: 18,
        p: 0,
        mx: 0.25,
        position: 'relative',
        top: -3,
        borderRadius: '50%',
        cursor: 'pointer',
        lineHeight: 1,
        fontSize: 10,
        fontWeight: 700,
        border: `1.5px solid ${ring}`,
        backgroundColor: accepted ? ring : theme.palette.background.paper,
        color: accepted ? theme.palette.common.white : ring,
        boxShadow: selected ? `0 0 0 3px ${theme.palette.ocean[300]}` : 'none',
        transition: 'box-shadow .15s',
      }}
    >
      {n}
    </Box>
  );
});

const PaneLabel: FC<{ label: string; ai?: boolean; children?: React.ReactNode }> = observer(
  ({ label, ai, children }) => {
    const theme = useTheme();
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          maxWidth: 620,
          mx: 'auto',
        }}
      >
        {ai && <SparkleIcon size={13} color={theme.palette.ocean[700]} />}
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: ai ? theme.palette.ocean[700] : theme.palette.text.tertiary,
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            flex: 1,
            height: '1px',
            backgroundColor: ai ? theme.palette.ocean[300] : theme.palette.grey[300],
          }}
        />
        {children}
      </Box>
    );
  },
);

const Sheet: FC<{ children: React.ReactNode }> = observer(({ children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 1,
        px: { xs: 3, md: 6 },
        py: 5,
        maxWidth: 620,
        mx: 'auto',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}
    >
      {children}
    </Box>
  );
});

/**
 * The pristine upload, rendered from `sourceText`, with every passage the AI
 * would touch marked in place.
 *
 * Extraction hands back markdown, so it is rendered as such — headings, lists
 * and tables survive instead of collapsing into one run of text. GENERATE items
 * have no source at all, which the caller handles.
 *
 * The marks answer "what is the AI proposing to do to *this* document", which
 * the proposed pane alone cannot: reading a rewrite tells you what the new text
 * says, not which paragraph of the original it replaces. Removals are red and
 * rewordings orange, the same two colours the legend names; an addition has
 * nothing here to mark, since it did not exist yet.
 */
export const OriginalPane: FC<{
  sourceText: string;
  changes?: ProseChange[];
  order?: string[];
  selectedId?: string | null;
  onSelect?: (changeId: string | null) => void;
}> = observer(({ sourceText, changes = [], order = [], selectedId = null, onSelect }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const segments = useMemo(() => buildOriginalSegments(sourceText, changes), [sourceText, changes]);
  const hasMarks = segments.some((s) => s.change);
  const { scrollRef, targetRef } = useScrollToSelected(selectedId);

  const bodySx = {
    fontFamily: DOC_FONT,
    fontSize: 14,
    lineHeight: 1.85,
    color: 'text.secondary',
  } as const;

  return (
    <Box
      ref={scrollRef}
      sx={{ flex: 1, overflowY: 'auto', px: 3.5, py: 3, minWidth: 0 }}
      onClick={() => onSelect?.(null)}
    >
      <PaneLabel label={t('app.remediation.review.original')}>
        {hasMarks && <OriginalLegend />}
      </PaneLabel>
      <Sheet>
        {/* Each segment is its own markdown render, and every render zeroes its
            first and last margin — so without this the paragraphs either side of
            a mark would sit flush against it. `0.75em` is the paragraph gap
            markdown uses internally, which keeps the rhythm unbroken. */}
        <Box sx={{ ...bodySx, '& > * + *': { marginTop: '0.75em' } }}>
          {segments.map((segment, i) => {
            const change = segment.change;
            if (!change) return <Markdown key={i}>{segment.text}</Markdown>;

            const removed = change.type === 'deletion';
            const selected = selectedId === change.changeId;
            const n = order.indexOf(change.changeId) + 1;
            const heading = segment.kind === 'heading';

            return (
              <Box
                key={i}
                // The line the mark sits on. Vertical margin has no effect on an
                // inline box, so the spacing rhythm lives out here and the
                // highlight itself stays inline, hugging the text.
                sx={{
                  fontWeight: heading ? 700 : 400,
                  fontSize: heading ? '1.05em' : undefined,
                  paddingInlineStart: segment.kind === 'list_item' ? '1.4em' : 0,
                  textIndent: segment.kind === 'list_item' ? '-0.9em' : 0,
                }}
              >
                {segment.kind === 'list_item' && '• '}
                <Box
                  component="span"
                  ref={selected ? targetRef : undefined}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onSelect?.(change.changeId);
                  }}
                  sx={{
                    cursor: onSelect ? 'pointer' : 'default',
                    borderRadius: '3px',
                    px: selected ? 0.25 : 0,
                    outline: selected ? `2px solid ${theme.palette.ocean[700]}` : 'none',
                    outlineOffset: 2,
                    backgroundColor: selected ? theme.palette.ocean[100] : markTint(theme, removed),
                  }}
                >
                  {/* Inline: the block syntax is stripped and re-expressed by the
                      line above, so the highlight is not broken up by a <p> or
                      an <li> it cannot paint around. */}
                  <Markdown inline>{segment.text.slice(segment.marker?.length ?? 0)}</Markdown>
                  {n > 0 && (
                    <Pin
                      n={n}
                      decision={change.decision}
                      selected={selected}
                      onClick={() => onSelect?.(change.changeId)}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Sheet>
    </Box>
  );
});

/** Decodes the two mark colours, using the change-type labels the rail uses. */
const OriginalLegend: FC = observer(() => {
  const theme = useTheme();
  const { t } = useTrans();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
      {(
        [
          ['deletion', true],
          ['modification', false],
        ] as const
      ).map(([type, removed]) => (
        <Box key={type} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: '2px',
              border: `1px solid ${removed ? theme.palette.red[500] : theme.palette.orange[500]}`,
              backgroundColor: markTint(theme, removed),
            }}
          />
          <Typography sx={{ fontSize: 10, color: 'text.tertiary', whiteSpace: 'nowrap' }}>
            {t(`app.remediation.review.change.${type}` as SingularKeys)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
});

/**
 * The AI's version, with each proposed change highlighted inline and carrying a
 * numbered pin.
 *
 * `document.sections` already has every change applied, so a modification shows
 * the new text (struck through once rejected) and a deletion — which is absent
 * from the document by definition — is reinstated at its anchor so the reviewer
 * can see what would be removed.
 */
export const ProposedPane: FC<{
  content: ProseDocument;
  changes: ProseChange[];
  order: string[];
  selectedId: string | null;
  onSelect: (changeId: string | null) => void;
  /** Needed to fetch the run-scoped figures embedded in this document. */
  runId: string;
}> = observer(({ content, changes, order, selectedId, onSelect, runId }) => {
  const theme = useTheme();
  const { t } = useTrans();
  const { scrollRef, targetRef } = useScrollToSelected(selectedId);

  const byBlock = useMemo(() => {
    const m = new Map<string, ProseChange>();
    changes.forEach((c) => m.set(c.blockId, c));
    return m;
  }, [changes]);

  const deletions = useMemo(
    () => planDeletions(content.sections, changes),
    [content.sections, changes],
  );

  // Catalog sections are a compliance-catalog grouping, not document
  // structure, and a single numbered list routinely lands split across
  // several of them — one numbered item per section. Computing ordinals per
  // section would restart every run at the section boundary, so this has to
  // run over the document's whole block sequence, in document order, and
  // only restart where a real non-list block breaks the run.
  const listOrdinals = useMemo(
    () => computeListOrdinals(content.sections.flatMap((section) => section.blocks)),
    [content.sections],
  );

  const renderChangeText = (change: ProseChange, fallback: string) => {
    const rejected = change.decision === RemediationDecision.REJECTED;
    const selected = selectedId === change.changeId;
    const isDeletion = change.type === 'deletion';

    // A rejected modification reverts to `before`; a rejected addition vanishes
    // from the final document, so it is struck through here.
    const text = isDeletion
      ? (change.before ?? '')
      : rejected && change.type === 'modification'
        ? (change.before ?? change.after ?? fallback)
        : (change.after ?? fallback);

    const struck = isDeletion ? !rejected : rejected && change.type === 'addition';

    return (
      <Box
        component="span"
        ref={selected ? targetRef : undefined}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onSelect(change.changeId);
        }}
        sx={{
          cursor: 'pointer',
          borderRadius: '3px',
          px: selected ? 0.25 : 0,
          outline: selected ? `2px solid ${theme.palette.ocean[700]}` : 'none',
          outlineOffset: 2,
          backgroundColor: selected
            ? theme.palette.ocean[100]
            : struck
              ? 'rgba(232,71,73,0.08)'
              : isDeletion
                ? 'transparent'
                : 'rgba(27,158,92,0.10)',
          color: struck ? theme.palette.error.main : 'inherit',
          textDecoration: struck ? 'line-through' : 'none',
        }}
      >
        <Markdown inline>{text}</Markdown>
        <Pin
          n={order.indexOf(change.changeId) + 1}
          decision={change.decision}
          selected={selected}
          onClick={() => onSelect(change.changeId)}
        />
      </Box>
    );
  };

  return (
    <Box
      ref={scrollRef}
      sx={{ flex: 1, overflowY: 'auto', px: 3.5, py: 3, minWidth: 0 }}
      onClick={() => onSelect(null)}
    >
      <PaneLabel label={t('app.remediation.review.proposed')} ai />
      <Sheet>
        <Box sx={{ fontFamily: DOC_FONT, fontSize: 14, lineHeight: 1.95, color: 'text.primary' }}>
          {content.sections.map((section, si) => {
            return (
              <Box key={`${section.sectionKey ?? 's'}-${si}`} sx={{ mt: si === 0 ? 0 : 3 }}>
                {/* `sectionName` is a compliance-catalog taxonomy label attached to
                    the section server-side — it is not text the user's document
                    contains, so this pane, which mirrors the source document,
                    never prints it. The section's own heading block below is what
                    actually names it in the document body. */}
                {/* Deletions that belong to the section rather than to a block of
                    it — see planDeletions for which land where. */}
                {(deletions.sectionStart.get(si) ?? []).map((d) => (
                  <Box component="div" key={d.changeId} sx={{ m: 0, mb: 0.75 }}>
                    {renderChangeText(d, '')}
                  </Box>
                ))}
                {section.blocks.map((block) => {
                  const change = byBlock.get(block.blockId);
                  const trailing = deletions.afterBlock.get(block.blockId) ?? [];
                  const depth = Math.min(Math.max(block.level ?? 0, 0), MAX_LIST_LEVEL);
                  // A numbered item prints its run-relative ordinal (see
                  // `computeListOrdinals`); a bullet at depth 0 matches the
                  // document's own top-level marker, and a bullet nested under it
                  // switches to a hollow one — the same disc-then-circle the
                  // browser gives a plain nested `<ul>` in the original pane, so a
                  // sub-item does not read as a sibling of the item it belongs to.
                  const marker =
                    block.kind !== 'list_item'
                      ? ''
                      : block.list === 'number'
                        ? `${listOrdinals.get(block.blockId) ?? 1}. `
                        : depth >= 1
                          ? '○ '
                          : '• ';
                  return (
                    <Box key={block.blockId}>
                      <Box
                        // div, not p: a markdown table inside a <p> makes the
                        // browser close the paragraph early and lose the layout.
                        component="div"
                        sx={{
                          m: 0,
                          mb: 0.75,
                          pl: block.kind === 'list_item' ? LIST_INDENT[depth] : 0,
                          fontWeight: block.kind === 'heading' ? 700 : 400,
                        }}
                      >
                        {block.kind === 'diagram' ? (
                          // An image, not its id. The AI puts a storage key on the
                          // block; printing that told the reviewer nothing about
                          // what the document actually contains.
                          <RemediationFigure
                            runId={runId}
                            pngKey={block.diagram?.pngKey}
                            caption={block.text}
                            width={block.diagram?.width}
                            height={block.diagram?.height}
                          />
                        ) : block.kind === 'table' ? (
                          // The AI's edit contract never targets a table — it is
                          // read-only pass-through content here, so unlike the
                          // branch below it never checks `change`.
                          <RemediationTable table={block.table} />
                        ) : (
                          <>
                            {marker}
                            {change && change.type !== 'deletion' ? (
                              renderChangeText(change, block.text ?? '')
                            ) : (
                              <Markdown inline>{block.text}</Markdown>
                            )}
                          </>
                        )}
                      </Box>
                      {trailing.map((d) => (
                        <Box component="div" key={d.changeId} sx={{ m: 0, mb: 0.75 }}>
                          {renderChangeText(d, '')}
                        </Box>
                      ))}
                    </Box>
                  );
                })}
                {(deletions.sectionEnd.get(si) ?? []).map((d) => (
                  <Box component="div" key={d.changeId} sx={{ m: 0, mb: 0.75 }}>
                    {renderChangeText(d, '')}
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      </Sheet>
    </Box>
  );
});
